// bus.js — event bus della shell (§4): UNICO canale inter-modulo consentito.
// Puro (zero DOM), testato con node --test (server/src/lib/shell-bus.test.js, B2.10).
//
// Catalogo CHIUSO v1: un evento nuovo = una riga in EVENTS + un caso in B2.10.
// emit con tipo fuori catalogo: THROW in dev/staging, console.error + scarto in prod.
// replay: chi si iscrive DOPO l'ultimo emit riceve subito l'ultimo valore
// (es. il Rail nato dopo vede l'ultimo nav:badge).

export const EVENTS = {
  'shell:ready':          { replay: true },   // {modules:[id]}
  'shell:module-changed': { replay: true },   // {id, prev}
  'shell:navigate':       { replay: false },  // {module, item?, params?} — richiesta cross-modulo
  'nav:badge':            { replay: true },   // {module, item?, count}
  'auth:changed':         { replay: true },   // {logged:boolean}
  'data:changed':         { replay: false },  // {entity?, source} — dal wrap di rerenderAll (protocollo 2.3)
  'toast':                { replay: false },  // {kind:'info'|'ok'|'error', msg}
};

// dev/staging = localhost/127.0.0.1 oppure localStorage.shell_dev==='1' (design §3).
export function defaultIsDev(win) {
  try {
    const w = win || (typeof window !== 'undefined' ? window : null);
    if (!w) return false;
    const h = (w.location && w.location.hostname) || '';
    if (h === 'localhost' || h === '127.0.0.1') return true;
    return !!(w.localStorage && w.localStorage.getItem('shell_dev') === '1');
  } catch (e) {
    return false;
  }
}

export function createBus(events = EVENTS, opts = {}) {
  const isDev = opts.isDev || (() => defaultIsDev(opts.win));
  const logger = opts.logger || console;
  const handlers = new Map();   // type -> Set<fn>
  const lastByType = new Map(); // type -> {type, payload, ts}

  function known(type) {
    return Object.prototype.hasOwnProperty.call(events, type);
  }

  // Tipo fuori catalogo: throw in dev/staging, console.error + scarto in prod.
  function badType(type, ctx) {
    const msg = '[bus] evento fuori catalogo: "' + type + '" (' + ctx + ') — aggiungi la riga in EVENTS (§4) e il caso in B2.10';
    if (isDev()) throw new Error(msg);
    logger.error(msg);
  }

  function on(type, fn, onOpts = {}) {
    if (!known(type)) { badType(type, 'on'); return () => {}; }
    const replay = 'replay' in onOpts ? !!onOpts.replay : !!events[type].replay;
    if (!handlers.has(type)) handlers.set(type, new Set());
    handlers.get(type).add(fn);
    if (replay && lastByType.has(type)) {
      try { fn(lastByType.get(type).payload); } catch (e) { logger.error('[bus] handler (replay) ' + type, e); }
    }
    return function off() {
      const s = handlers.get(type);
      if (s) s.delete(fn);
    };
  }

  function once(type, fn) {
    const off = on(type, function onceFn(payload) { off(); fn(payload); }, { replay: false });
    return off;
  }

  // SINCRONO; try/catch PER handler: un handler che lancia non blocca gli altri.
  function emit(type, payload) {
    if (!known(type)) { badType(type, 'emit'); return; }
    lastByType.set(type, { type, payload, ts: Date.now() });
    const s = handlers.get(type);
    if (!s || s.size === 0) return;
    Array.from(s).forEach((fn) => {
      try { fn(payload); } catch (e) { logger.error('[bus] handler ' + type, e); }
    });
  }

  function last(type) {
    if (!known(type)) { badType(type, 'last'); return null; }
    return lastByType.get(type) || null;
  }

  // Primitiva dei contract test B2: attende un evento che soddisfa predicate.
  // Se l'evento è replay e l'ultimo valore soddisfa già il predicate → risolve subito.
  function waitFor(type, predicate = () => true, timeoutMs = 10000) {
    if (!known(type)) {
      badType(type, 'waitFor'); // in dev THROWA sincrono
      return Promise.reject(new Error('[bus] waitFor su tipo fuori catalogo: ' + type));
    }
    const lastEv = lastByType.get(type);
    if (events[type].replay && lastEv && predicate(lastEv.payload)) {
      return Promise.resolve(lastEv.payload);
    }
    return new Promise((resolve, reject) => {
      let timer = null;
      const off = on(type, (payload) => {
        let ok = false;
        try { ok = !!predicate(payload); } catch (e) { ok = false; }
        if (!ok) return;
        if (timer) clearTimeout(timer);
        off();
        resolve(payload);
      }, { replay: false });
      timer = setTimeout(() => {
        off();
        reject(new Error('[bus] waitFor timeout (' + timeoutMs + 'ms) su ' + type));
      }, timeoutMs);
    });
  }

  return { on, once, emit, last, waitFor, EVENTS: events };
}
